import moment  from "moment";
import momentTz from "moment-timezone";
import qs from "qs";
import unidecode from 'unidecode';

import {COTLangExec, COTLangOutput, COTLangValue} from '../COTLangExec'
import { IToken}  from "ebnf";
import { FunctionsCode } from "./functions.code";

// COTLang Internal
export async function _literal(parser: COTLangExec, ast: IToken) {
    // only `'s are escaped in ``` string ```
    return String(ast.text).replace(/\\`/g, '`')
}

export async function _token(parser: COTLangExec, ast: IToken) {
    // e.g., convert '\\[' into '['
    // "=" | "|" | "(" | ")" | "[" | "]" | "#" | #x5C | #x2F | #x62 | #x66 | #x6E | #x72 | #x74 | #x75
    return ast.text.replace(/\\([=|()\[\]#`])/g, '$1');
}

function isTransformationAST (ast: IToken): boolean {
    if (ast.type === 'function') return true;
    // This might not be true, we should check for passthrough types
    if (ast.children.length === 1) return isTransformationAST(ast.children[0]);
    return false;
}

async function applyPipe(parser: COTLangExec, startTarget: any, pipes: IToken[], value: any, outputs: COTLangOutput) {
    // This function might throw.
    let target = startTarget;
    for (let i = 0; i < pipes.length; i += 1) {
        const isTransformation = isTransformationAST(pipes[i]);

        const key = await parser.exec(pipes[i], value, outputs, target);
        if (isTransformation) {
            target = key;
        } else {
            if (!target) {
                return undefined;
            }
            target = target[key];
        }
    }
    return target;
}

export async function _pipe(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    const target = await parser.exec(ast.children[0], value, outputs);
    const pipes = ast.children.filter((_, i) => i > 0);
    return await applyPipe(parser, target, pipes, value, outputs);
}

export async function passthrough(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    if (ast.children.length !== 1) throw new Error('Unexpected children at ' + ast.text);
    return await parser.exec(ast.children[0], value, outputs, pipeContext);
}

// COTLang Commands
export async function $join(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // JOIN REQUIRES 3+ arguments
    const str = [];
    const args = ast.children[0].children;
    const emptyDivider = ast.text.match(/\$JOIN##/);
    const divider = emptyDivider ? '' : await parser.exec(args[0], value, outputs);
    const start = emptyDivider ? 0 : 1;
    for (let i = start; i < args.length; i += 1) {
        str.push(await parser.exec(args[i], value, outputs))
    }
    return str.join(divider);
}

export async function $code(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    const args = ast.children[0].children;
    if (args.length < 3) throw new Error('$CODE requires at least 3 arguments');

    const model   = await parser.exec(args[0], value, outputs);
    const variant = await parser.exec(args[1], value, outputs);
    const code1   = await parser.exec(args[2], value, outputs);
    const code2   = args.length > 3 ? await parser.exec(args[3], value, outputs) : undefined;
    const code3   = args.length > 4 ? await parser.exec(args[4], value, outputs) : undefined;

    const functionsCode = new FunctionsCode(parser.baseURL);

    return await functionsCode.code({ Authorization: parser.authorization }, model, variant, code1, code2, code3);
}

export async function $value(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // USAGE: $VALUE#<valuePath>
    //    Replaces field with externalData's field, it receives value path directly
    //    e.g., $VALUE#user ---> will replace $VALUE with externalData.user
    //    e.g., $VALUE#channel|nameDisplay ---> will replace $VALUE with channel nameDisplay (channel must be populated)
    //    e.g., $VALUE#channel|userIds|[map=>email] ---> will replace $VALUE with Array<user.email> (channel and userIds must be populated)
    //    e.g., $VALUE#channel|userIds|[filter=>email=em@cotalker.com] will replace $VALUE with Array<user.email> where user.email === em@cotalker.com

    let result;
    // TODO This can be refactored, once passthrough types are defined.
    const arg: IToken = ast.children[0];
    const expression: IToken = arg.children[0];
    const child: IToken = expression.children[0];

    if (child.type === 'pipes') {
        // CASE 1: Merge Pipes
        // $VALUE#p1|p2 = externalData|p1|p2
        const pipes = child.children;
        result = await applyPipe(parser, value, pipes, value, outputs);
    } else {
        // CASE 2: Independent path
        // $VALUE#(p1|p2) = externalData|(p1|p2)
        if (!value) {
            return undefined;
        }
        result = value[await parser.exec(child, value, outputs, pipeContext)];
    }
    return result;
}

export async function $output(parser: COTLangExec, ast: IToken, value: COTLangValue, outputs: COTLangOutput, pipeContext?: any) {
    // USAGE: $OUTPUT#<stage-name>#<valuePath>
    //    Replaces field with other pb outputs
    //    e.g., $OUTPUT#createuser#_id---> will replace $OUTPUT with the created user _id
    //    for advance usage view $VALUE documentation
        const keyName = await parser.exec(ast.children[0], value, outputs);
        if (!keyName || typeof keyName !== 'string') {
            throw new Error('ERROR: keyName must be a string');
        }

        const tempTarget = outputs.find(o => o.name === keyName || o.key === keyName);
        if (!tempTarget) return undefined;

        const target = tempTarget.result;
        let result;

        // TODO This can be refactored, once passthrough types are defined.
        const arg: IToken = ast.children[1];
        const expression: IToken = arg.children[0];
        const child: IToken = expression.children[0];

        if (child.type === 'pipes') {
            // CASE 1: Merge Pipes
            // $OUTPUT#key#p1|p2 = externalData|p1|p2
            const pipes = child.children;
            result = await applyPipe(parser, target, pipes, value, outputs);
        } else {
            // CASE 2: Independent path
            // $OUTPUY#key#(p1|p2) = externalData|(p1|p2)
            if (!target) {
                return undefined;
            }
            result = target[await parser.exec(child, target, outputs, pipeContext)];
        }

        return result;
}

export async function $data(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // @deprecated use $VALUE instead.
    return await $value(parser, ast, value, outputs);
}

export async function $env(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    const whitelist = ['BASEURL', 'FAASURL'];
    const envName = await parser.exec(ast.children[0], value, outputs);
    if (typeof envName === 'string' && whitelist.includes(envName)) {
        return parser.environment[String(envName)];
    }
    return undefined;
}

export async function $$time(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // USAGE: $$TIME#<interval>#<count>
    //    Sets a relative date from execution time.
    //    e.g., $$TIME#days#-7 ==> moment().add(-7, 'days');
    //    e.g., $$TIME#months#1 ---> Set now + 1 month.
    const interval = await parser.exec(ast.children[0], value, outputs);
    const count = await parser.exec(ast.children[1], value, outputs);
    return moment().add(count, interval).toDate().getTime();
}


// COTLang Function
export async function size(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
  const target = pipeContext;
  if (Array.isArray(target) ||
      typeof target === 'string') {
    return target.length;
  }
  return undefined;
}

export async function toString(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (Array.isArray(target)) {
        if (key === 'JSON') {
            return JSON.stringify(target);
        }
        return target.join(key === '*' ? ', ' : key);
    }
    return String(target);
}

export async function cast(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#anyValue|[cast=>parseFloat]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    let result = target; // if target is a mutable object, an object copy should be used
    switch (key) {
        case 'string':
            return String(result);
        case 'parseInt':
            result = parseInt(result, 10);
            if (isNaN(result)) return undefined;
            break;
        case 'parseFloat':
            result = parseFloat(result);
            if (isNaN(result)) return undefined;
            break;
        case 'boolean': {
            if (!result) {
                return false;
            }
            if (typeof result === 'string') {
                return !!result.match(/^(false|0)$/i);
            }
            result = Boolean(result);
            break;
        }
        default:
            throw new Error(`Key not implemented ${key} :: ${target}`);
    }
    return result;
}

export async function map(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#populatedChannelUsers|[map=>email]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (Array.isArray(target)) {
        return target.map(t => t[key]);
    }
    return target;
}

export async function arrayToObject(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[arrayToObject=>key=value]
    // converts answer.data: [DataQuestion1, DataQuestion2,...] to {DataQuestion1.key: DataQuestion1.value, DataQuestion2.key: DataQuestion2.value, ...}
    // if value === "*" this function returns to {DataQuestion1.key: DataQuestion1, DataQuestion2.key: DataQuestion2, ...}
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (Array.isArray(target)) {
        if (valueToCompare === '*') {
            return target.reduce((obj, item) => {
                if (Object.prototype.toString.call(item) === '[object Object]' && Object.prototype.hasOwnProperty.call(item, key)) {
                    return Object.assign(obj, { [item[key]]: item });
                }
                return obj;
            }, {});
        }
        return target.reduce((obj, item) => {
            if (Object.prototype.toString.call(item) === '[object Object]' && Object.prototype.hasOwnProperty.call(item, key)) {
                return Object.assign(obj, { [item[key]]: item[valueToCompare] });
            }
            return obj;
        }, {});
    }

    return undefined;
}

export async function isDefinedFilter(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[isDefinedFilter=>meta]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (Array.isArray(target)) {
        return target.filter(t => !!t[key]);
    }

    return undefined;
}

const smartCompare = (lhs: any, rhsInput: any): boolean => {
    let rhs = rhsInput;
    if (typeof lhs !== rhs) {
        if (typeof lhs === 'string') rhs = String(rhs);
        if (typeof lhs === 'number') rhs = parseFloat(rhs);
    }
    return lhs === rhs
}

export async function filter(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[filter=>contentType=application/vnd.cotalker.survey+textnumber]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (Array.isArray(target)) {
        return target.filter(t => smartCompare(t[key], valueToCompare));
    }

    return undefined;
}

export async function filterIndex(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // [filterIndex=>key]
    // Filter and return indexes that match the valueToCompare string.
    //
    // e.g., $VALUE#data|0|responses|[filterIndex=__ON__]
    // With $VALUE = { data: [ { responses: ["__ON__", "__OFF__", "__ON__"] } ] }
    // return [0,2]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (Array.isArray(target)) {
        return target
            .map((t, index) => ({ object: t, index }))
            // TODO Smart casting will be required for non-text
            .filter(wrapper => smartCompare(wrapper.object, key))
            .map(wrapper => wrapper.index);
    }

    return undefined;
}

export async function notEqualFilter(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[notEqualFilter=>contentType=application/vnd.cotalker.survey+textnumber]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (Array.isArray(target)) {
        return target.filter(t => !(smartCompare(t[key], valueToCompare)));
    }

    return undefined;
}

export async function simpleNotEqualFilter(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[simpleNotEqualFilter=>5da730f9c4d81bbe34e9111e]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (Array.isArray(target)) {
        // eslint-disable-next-line eqeqeq
        // tslint:disable-next-line:triple-equals
        return target.filter(t => t != key); // be careful, not an strict comparator
    }

    return target;
}

export async function simpleEqualFilter(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[simpleEqualFilter=>5da730f9c4d81bbe34e9111e]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (Array.isArray(target)) {
        // eslint-disable-next-line eqeqeq
        // tslint:disable-next-line:triple-equals
        return target.filter(t => t == key); // be careful, not an strict comparator
    }
    return target;
}

export async function concat(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[find=>contentType=application/vnd.cotalker.survey+textnumber]|process|[concat=>array=($VALUE|anyArray)]
    let target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (target === undefined || target === null) {
        target = [];
    }

    if (Array.isArray(target)) {
        switch (key) {
            case 'array':
                if (!Array.isArray(valueToCompare)) {
                    throw new Error(`Not valid array value ${key} :: ${target} :: ${valueToCompare}`);
                }
                return target.concat(valueToCompare);
            default:
                throw new Error(`Key not implemented ${key} :: ${target} :: ${valueToCompare}`);
        }
    }
    return target;
}

export async function find(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[find=>identifier=minuta-fecha]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (Array.isArray(target)) {
        return target.find(t => smartCompare(t[key], valueToCompare));
    }

    return undefined;
}

export async function push(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#populatedChannelUsers|[map=>email]|[push=>string=ignacio@cotalker.com]
    // push: (target: any, key: string, valueToCompare: any) => {
    let target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (target === undefined || target === null) {
        target = [];
    }

    if (Array.isArray(target)) {
        switch (key) {
            case 'string':
                target.push(String(valueToCompare));
                break;
            case 'number':
                target.push(parseFloat(valueToCompare));
                break;
            default:
                throw new Error(`Key not implemented ${key} :: ${target} :: ${valueToCompare}`);
        }
    }

    return target;
}

export async function add(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#populatedChannelUsers|[map=>email]|[add=>string=ignacio@cotalker.com]
    let target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (target === undefined || target === null) {
        target = [];
    }

    if (Array.isArray(target)) {
        switch (key) {
            case 'string': {
                const lvalue = String(valueToCompare);
                if (!(target.includes(lvalue))) target.push(lvalue);
                break;
            }
            case 'number': {
                const lvalue = parseFloat(valueToCompare);
                if (!(target.includes(lvalue))) target.push(lvalue);
                break;
            }
            default:
                throw new Error(`Key not implemented ${key} :: ${target} :: ${valueToCompare}`);
        }
    }
    return target;
}

export async function math(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#answer|anyNumber|[math=>add=2]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (isNaN(target)) throw new Error(`Target ${String(target)} is not a valid number.`);
    if (isNaN(valueToCompare)) throw new Error(`Value ${String(target)} is not a valid number.`);
    let result = parseFloat(target);
    const lvalue = parseFloat(valueToCompare);
    switch (key) {
        case 'add': {
            result += lvalue;
            break;
        }
        case 'subtract': {
            result -= lvalue;
            break;
        }
        default:
            throw new Error(`Key not implemented ${key} :: ${target} :: ${valueToCompare}`);
    }
    return result;
}

export async function date(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#createdAt|[date=>format=DD-MM-YYYY] or $VALUE#createdAt|[date=>format=DD-MM-YYYY@America/Santiago]
    // target=createdAt, key=format, valueToCompare=DD-MM-YYYY
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    const splittedValue = valueToCompare.split('@');
    let returnValue;
    if (isNaN(Number(String(target)))) {
        // case date in string format (Ex: Thu, 03 Mar 2016 21:18:39 +0000 or null)
        returnValue = moment(new Date(String(target)));
    } else {
        // case date in numeric format (Ex: 1571832000000)
        returnValue = moment(new Date(Number(String(target))));
    }
    if (!moment(returnValue).isValid()) {
        return undefined
    }

    switch (key) {
        case 'format':
            return splittedValue[1] ? momentTz.tz(returnValue, splittedValue[1]).format(splittedValue[0]) : returnValue.format(valueToCompare);
        default:
            throw new Error(`Operator ${key} not implemented in method :: ${target} :: ${valueToCompare}`);
    }
}

export async function every(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#data|[filter=>contentType=application/vnd.cotalker.survey+textnumber]|[map=>responses]|[every=>lte=2]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (Array.isArray(target)) {
        switch (key) {  // similar to mongo comparison operators
            case 'gte':
                return target.every(t => t >= valueToCompare);
            case 'gt':
                return target.every(t => t > valueToCompare);
            case 'lt':
                return target.every(t => t < valueToCompare);
            case 'lte':
                return target.every(t => t <= valueToCompare);
            case 'eq': // be careful, not an strict comparator
                // tslint:disable-next-line:triple-equals
                return target.every(t => t == valueToCompare); // eslint-disable-line eqeqeq
            default:
                throw new Error(`Operator ${key} not implemented in method :: ${target} :: ${valueToCompare}`);
        }
    }
    return target;
}

export async function some (parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // example $VALUE#data|[filter=>contentType=application/vnd.cotalker.survey+textnumber]|[map=>responses]|[some=>lte=2]
    // some: (target: any, key: string, valueToCompare: any) => {
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    const valueToCompare = await parser.exec(ast.children[2], value, outputs);

    if (Array.isArray(target)) {
        switch (key) { // similar to mongo comparison operators
            case 'gte':
                return target.some(t => t >= valueToCompare);
            case 'gt':
                return target.some(t => t > valueToCompare);
            case 'lt':
                return target.some(t => t < valueToCompare);
            case 'lte':
                return target.some(t => t <= valueToCompare);
            case 'eq': // be careful, not an strict comparator
                // tslint:disable-next-line:triple-equals
                return target.some(t => t == valueToCompare); // eslint-disable-line eqeqeq
            default:
                throw new Error(`Operator ${key} not implemented in method :: ${target} :: ${valueToCompare}`);

        }
    }
    return target;
}

function safeJSONParse(input: any): any {
    try {
        return JSON.parse(input);
    } catch (e) {
        return undefined;
    }
}
function safeJSONStringify(input: any): string | undefined {
    try {
        return JSON.stringify(input);
    } catch (e) {
        return undefined;
    }
}

export async function json(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g. $VALUE#populatedChannelUsers|[json=>parse]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (key === 'parse') {
        if (Array.isArray(target)) {
            return target.map(x => safeJSONParse(x));
        } else {
            return safeJSONParse(target);
        }
    }

    if (key === 'stringify') {
        if (Array.isArray(target)) {
            return target.map(x => safeJSONStringify(x));
        } else {
            return safeJSONStringify(target);
        }
    }

    return undefined;
}

export async function querystring(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g., $VALUE#someValue|[querystring=>string]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    if (key === 'escape') {
        return encodeURIComponent(target);
    } else if (key === 'string') {
        // convert 'test=sámple\nok!' to 'test=s%C3%A1mple%0Aok!'
        const parts: string[] = target.split('&');
        const object: { [key: string]: string } = {};
        parts.forEach((item) => {
            const keyvalue = item.split('=');
            if (keyvalue.length === 2) {
                object[keyvalue[0]] = keyvalue[1];
            }
        });
        return qs.stringify(object);
    }
    // target should be object. E.g, { test: 'sámple\nok!' }
    if (typeof target === 'object') {
        return qs.stringify(target);
    }

    return undefined;
}

export async function cotanswer_list(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g., $VALUE#answer_data|[cotanswer_list=>array]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);
    /* Target expected format:
        {
          "process" : ["a"],
          "responses" : ["__ON__", "__OFF__"],
          "display" : ["red", "yellow"],
          "code" : ["a", "b"],
        }
     */
    if (key === 'array') {
        const output: string[] = [];
        if (!target.responses || !Array.isArray(target.responses)) return output;
        if (!target.display || !Array.isArray(target.display)) return output;
        for (let i = 0; i < target.responses.length && i < target.display.length; i += 1) {
            if (target.responses[i] === '__ON__') {
                output.push(target.display[i]);
            }
        }
        return output;
    }
    return undefined;
}

export async function gencode(parser: COTLangExec, ast: IToken, value: any, outputs: any[], pipeContext?: any) {
    // e.g., $VALUE#path|to|string[gencode=>*]
    const target = pipeContext;
    const key = await parser.exec(ast.children[1], value, outputs);

    // new Date().getTime().toString(16) => 11 chars => 174 9c5c 522f
    const getTime = () => new Date().getTime().toString(16);

    // crypto.randomBytes(4).toString('hex') => 8 => 2305 2a97
    const getRandom = (length = 8) =>
        Array(Math.round(length/2))
            .fill(null)
            .map(() => (Math.round(Math.random() * 100000))
                .toString(16)
                .substr(0, 2)
                .padStart(2, '0'))
            .join('');

    const transform = (s: string) => unidecode(s)
        .toLocaleLowerCase()
        .substr(0, 48)
        .replace(/[^a-z0-9_]/g,'_')
        .replace(/^[^a-z]/g,'x');

    if (!target) {
        return `cot_${getRandom()}_${getTime()}`;
        // cot_ _ => 5 chars
        // Total: 24 chars
    }

    if (Array.isArray(target)) {
        const code = transform(target.join(key || ''));
        return `${code}_${getTime()}`;
    }

    const code2 = transform(String(target));
    return `${code2}_${getTime()}`;
}
